import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

import Loading from "../common/loading";

const DEFAULT_PAGE_SIZE = 10;

function authHeaders() {
  return {
    Authorization: "Bearer " + localStorage.getItem("token"),
  };
}

function getDeviceSerial(device = {}) {
  const serial = device.enclosureSerialNumber ?? device.serial;
  return serial == null ? "" : String(serial);
}

function normalizeDevice(device = {}) {
  const serial = getDeviceSerial(device);
  return {
    ...device,
    serial,
    name:
      device.deviceName ||
      device.name ||
      device.last_known_name ||
      (serial ? `PDU ${serial}` : "PDU"),
    modelNumber: device.modelNumber || device.model || "",
    connectionState: device.connectionState || "",
  };
}

function buildDeviceDirectory(devices = []) {
  return (devices || []).reduce((sum, device) => {
    const normalized = normalizeDevice(device);
    if (normalized.serial) {
      sum[normalized.serial] = normalized;
    }
    return sum;
  }, {});
}

function mergeDeviceDirectory(current, devices = []) {
  return {
    ...(current || {}),
    ...buildDeviceDirectory(devices),
  };
}

function deviceLabel(device = {}) {
  const serial = device.serial || getDeviceSerial(device);
  const model = device.modelNumber ? `SP-${device.modelNumber}` : "PDU";
  const name = device.name || device.deviceName || "";
  return `${model} (${serial})${name ? ` ${name}` : ""}`;
}

export default function DeviceAssignmentPicker({
  selectedDeviceIds = [],
  onSelectedDeviceIdsChange = () => {},
  initialDevices = [],
  editable = true,
  endpoint = "/v2/devices",
  queryParams = {},
  pageSize = DEFAULT_PAGE_SIZE,
  selectedTitle = "Selected individual PDUs",
  availableTitle = "Add individual PDUs",
  emptySelectedText = "No individual PDUs selected.",
  searchPlaceholder = "Search by model number, device name, or serial number",
}) {
  const [deviceDirectory, setDeviceDirectory] = useState(() =>
    buildDeviceDirectory(initialDevices),
  );
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [options, setOptions] = useState([]);
  const [meta, setMeta] = useState({
    currentPage: 1,
    limit: pageSize,
    totalCount: 0,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  const selectedKeys = useMemo(
    () => selectedDeviceIds.map((id) => String(id)).filter(Boolean),
    [selectedDeviceIds],
  );
  const selectedSet = useMemo(() => new Set(selectedKeys), [selectedKeys]);
  const queryKey = useMemo(() => JSON.stringify(queryParams || {}), [queryParams]);
  const requestQueryParams = useMemo(() => {
    try {
      return JSON.parse(queryKey);
    } catch (error) {
      return {};
    }
  }, [queryKey]);

  useEffect(() => {
    setDeviceDirectory((current) => mergeDeviceDirectory(current, initialDevices));
  }, [initialDevices]);

  useEffect(() => {
    if (!editable) {
      return undefined;
    }

    const source = axios.CancelToken.source();
    setLoading(true);
    setLoadError("");

    axios
      .get(endpoint, {
        headers: authHeaders(),
        cancelToken: source.token,
        params: {
          ...requestQueryParams,
          page,
          limit: pageSize,
          ...(search ? { search } : {}),
        },
      })
      .then((res) => {
        const nextOptions = (res?.data?.data || []).map(normalizeDevice);
        setOptions(nextOptions);
        setMeta(
          res?.data?.meta || {
            currentPage: page,
            limit: pageSize,
            totalCount: nextOptions.length,
            totalPages: nextOptions.length ? 1 : 0,
          },
        );
        setDeviceDirectory((current) =>
          mergeDeviceDirectory(current, nextOptions),
        );
      })
      .catch((error) => {
        if (axios.isCancel(error)) {
          return;
        }
        setOptions([]);
        setMeta({
          currentPage: page,
          limit: pageSize,
          totalCount: 0,
          totalPages: 0,
        });
        setLoadError("Unable to load available devices.");
      })
      .finally(() => {
        if (!source.token.reason) {
          setLoading(false);
        }
      });

    return () => {
      source.cancel("Device assignment picker request canceled");
    };
  }, [editable, endpoint, page, pageSize, requestQueryParams, search]);

  const selectedDevices = selectedKeys.map((serial) => ({
    serial,
    ...(deviceDirectory[serial] || {}),
    name: deviceDirectory[serial]?.name || `PDU ${serial}`,
  }));

  const totalPages =
    Number(meta.totalPages) ||
    (Number(meta.totalCount) ? Math.ceil(Number(meta.totalCount) / pageSize) : 0);
  const hasNextPage = totalPages ? page < totalPages : false;
  const hasPreviousPage = page > 1;

  const addDevice = (serial) => {
    const key = String(serial);
    if (!key || selectedSet.has(key)) {
      return;
    }
    onSelectedDeviceIdsChange([...selectedKeys, key]);
  };

  const removeDevice = (serial) => {
    const key = String(serial);
    onSelectedDeviceIdsChange(selectedKeys.filter((id) => id !== key));
  };

  const applySearch = () => {
    setPage(1);
    setSearch(searchInput.trim());
  };

  return (
    <div style={styles.wrapper}>
      <div style={styles.section}>
        <div style={styles.sectionTitle}>{selectedTitle}</div>
        {selectedDevices.length === 0 ? (
          <div style={styles.emptyText}>{emptySelectedText}</div>
        ) : (
          <div style={styles.selectedList}>
            {selectedDevices.map((device) => (
              <span key={device.serial} style={styles.selectedPill}>
                <span>{deviceLabel(device)}</span>
                {editable ? (
                  <button
                    type="button"
                    style={styles.pillButton}
                    onClick={() => removeDevice(device.serial)}
                    aria-label={`Remove PDU ${device.serial}`}
                  >
                    <i className="material-icons" style={styles.pillIcon}>
                      close
                    </i>
                  </button>
                ) : null}
              </span>
            ))}
          </div>
        )}
      </div>

      {editable ? (
        <div style={styles.section}>
          <div style={styles.sectionTitle}>{availableTitle}</div>
          <div style={styles.searchRow}>
            <input
              type="text"
              value={searchInput}
              placeholder={searchPlaceholder}
              style={styles.searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  applySearch();
                }
              }}
            />
            <button
              type="button"
              className="button secondary"
              style={styles.searchButton}
              onClick={applySearch}
            >
              <i className="material-icons" style={styles.buttonIcon}>
                search
              </i>
              Search
            </button>
          </div>

          {loadError ? <div style={styles.errorText}>{loadError}</div> : null}

          {loading && options.length === 0 ? (
            <div style={styles.loadingWrap}>
              <Loading black />
            </div>
          ) : (
            <div style={styles.optionList}>
              {options.length === 0 && !loading ? (
                <div style={styles.emptyText}>No devices found.</div>
              ) : null}
              {options.map((device) => {
                const isSelected = selectedSet.has(device.serial);
                return (
                  <div key={device.serial} style={styles.optionRow}>
                    <div style={styles.optionCopy}>
                      <div style={styles.optionTitle}>{deviceLabel(device)}</div>
                      <div style={styles.optionMeta}>
                        {device.connectionState || "Unknown status"}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="button secondary"
                      disabled={isSelected}
                      style={{
                        ...styles.rowButton,
                        ...(isSelected ? styles.rowButtonDisabled : {}),
                      }}
                      onClick={() => addDevice(device.serial)}
                    >
                      <i className="material-icons" style={styles.buttonIcon}>
                        {isSelected ? "done" : "add"}
                      </i>
                      {isSelected ? "Added" : "Add"}
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          <div style={styles.pager}>
            <div style={styles.pagerSummary}>
              {Number(meta.totalCount) > 0
                ? `Page ${page} of ${totalPages || 1} (${meta.totalCount} devices)`
                : "No matching devices"}
            </div>
            <div style={styles.pagerActions}>
              <button
                type="button"
                className="button secondary"
                style={styles.pagerButton}
                disabled={!hasPreviousPage || loading}
                onClick={() => setPage((current) => Math.max(current - 1, 1))}
              >
                Previous
              </button>
              <button
                type="button"
                className="button secondary"
                style={styles.pagerButton}
                disabled={!hasNextPage || loading}
                onClick={() => setPage((current) => current + 1)}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

const styles = {
  wrapper: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "14px",
  },
  section: {
    width: "100%",
  },
  sectionTitle: {
    fontSize: "13px",
    fontWeight: 600,
    color: "var(--app-text)",
    marginBottom: "8px",
  },
  selectedList: {
    display: "flex",
    flexWrap: "wrap",
    gap: "6px",
  },
  selectedPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    backgroundColor: "#223647",
    color: "white",
    borderRadius: "5px",
    fontSize: "13px",
    fontWeight: 500,
    padding: "3px 4px 3px 7px",
  },
  pillButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    border: "none",
    background: "transparent",
    color: "white",
    padding: "0",
    cursor: "pointer",
    width: "18px",
    height: "18px",
  },
  pillIcon: {
    fontSize: "14px",
    marginRight: "0",
  },
  emptyText: {
    color: "var(--text-soft-color, #6b7280)",
    fontSize: "13px",
  },
  searchRow: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
    marginBottom: "8px",
  },
  searchInput: {
    marginBottom: 0,
    minWidth: 0,
    flex: 1,
  },
  searchButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    marginBottom: 0,
    whiteSpace: "nowrap",
  },
  buttonIcon: {
    fontSize: "16px",
    margin: 0,
  },
  optionList: {
    border: "1px solid var(--border-color, #e5e7eb)",
    borderRadius: "6px",
    overflow: "hidden",
  },
  optionRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
    padding: "10px 12px",
    borderBottom: "1px solid var(--border-color, #e5e7eb)",
  },
  optionCopy: {
    minWidth: 0,
  },
  optionTitle: {
    fontSize: "13px",
    fontWeight: 600,
    color: "var(--app-text)",
    overflowWrap: "anywhere",
  },
  optionMeta: {
    fontSize: "12px",
    color: "var(--text-soft-color, #6b7280)",
    marginTop: "2px",
  },
  rowButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
    marginBottom: 0,
    padding: "7px 10px",
    whiteSpace: "nowrap",
  },
  rowButtonDisabled: {
    opacity: 0.65,
    cursor: "default",
  },
  loadingWrap: {
    padding: "16px",
  },
  errorText: {
    color: "var(--danger-color, #b71c1c)",
    fontSize: "13px",
    marginBottom: "8px",
  },
  pager: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
    marginTop: "10px",
    flexWrap: "wrap",
  },
  pagerSummary: {
    fontSize: "12px",
    color: "var(--text-soft-color, #6b7280)",
  },
  pagerActions: {
    display: "flex",
    gap: "8px",
  },
  pagerButton: {
    marginBottom: 0,
    padding: "7px 10px",
  },
};
